import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, Checkbox, Divider, Dropdown, Flex, Popover, Tag, Typography } from "antd"
import { MoreOutlined, PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { capitalizeFirstLetter } from "utils/data-formatters"
import { formatCurrency } from "./useDetailsColumns"
import styles from "./DetailsCardList.module.css"

const OPTIONAL_FIELD_KEYS = ["booking_source", "taxes_and_fees", "booking_total_amount", "base_cost"]

function Field({ label, children }) {
  return (
    <div className={styles.field}>
      <Typography.Text type="secondary" className={styles.fieldLabel}>
        {label}
      </Typography.Text>
      <div className={styles.fieldValue}>{children}</div>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node
}

function ActionMenu({ record, onEdit, onDelete }) {
  const { t } = useTranslation()
  return (
    <Dropdown
      menu={{
        items: [
          { key: "edit", label: t("accounting.details.edit") },
          { key: "delete", label: t("accounting.details.delete"), danger: true }
        ],
        onClick: ({ key }) => {
          if (key === "edit") onEdit(record)
          if (key === "delete") onDelete(record)
        }
      }}
      trigger={["click"]}
    >
      <Button type="text" size="small" icon={<MoreOutlined />} />
    </Dropdown>
  )
}

ActionMenu.propTypes = {
  record: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

function StatusTag({ status }) {
  if (!status) return null
  const color = status.toLowerCase().includes("paid") ? "success" : "default"
  return <Tag color={color}>{capitalizeFirstLetter(status.replaceAll("_", " "))}</Tag>
}

StatusTag.propTypes = {
  status: PropTypes.string
}

function SalesCard({ record, onEdit, onDelete, visibleOptional }) {
  const { t } = useTranslation()

  const show = (key) => visibleOptional.has(key)

  const fields = [
    record.date && {
      key: "date",
      label: t("accounting.details.columns.transaction_date"),
      value: <Typography.Text>{record.date}</Typography.Text>
    },
    {
      key: "transaction_id",
      label: t("accounting.details.columns.transaction_id"),
      value: (
        <Flex align="center" gap={6} wrap>
          <Typography.Text>{record.transaction_id || "-"}</Typography.Text>
          {record.payment_status && <StatusTag status={record.payment_status} />}
        </Flex>
      )
    },
    record.amount != null && {
      key: "amount",
      label: t("accounting.details.columns.payment_amount"),
      value: <Typography.Text>{record.formatted_amount || formatCurrency(record.amount)}</Typography.Text>
    },
    record.payment_method && {
      key: "payment_method",
      label: t("accounting.details.columns.payment_method"),
      value: <Tag>{capitalizeFirstLetter(record.payment_method)}</Tag>
    },
    show("booking_source") && record.booking_source && {
      key: "booking_source",
      label: t("accounting.details.columns.booking_source"),
      value: <Typography.Text>{capitalizeFirstLetter(record.booking_source)}</Typography.Text>
    },
    show("taxes_and_fees") && record.taxes_and_fees != null && {
      key: "taxes_and_fees",
      label: t("accounting.details.columns.taxes_and_fees"),
      value: <Typography.Text>{formatCurrency(record.taxes_and_fees)}</Typography.Text>
    },
    show("booking_total_amount") && record.booking_total_amount != null && {
      key: "booking_total_amount",
      label: t("accounting.details.columns.total_amount"),
      value: <Typography.Text strong>{formatCurrency(record.booking_total_amount)}</Typography.Text>
    },
    show("base_cost") && record.base_cost != null && {
      key: "base_cost",
      label: t("accounting.details.columns.base_cost"),
      value: <Typography.Text>{formatCurrency(record.base_cost)}</Typography.Text>
    }
  ].filter(Boolean)

  return (
    <div className={styles.card}>
      <Flex justify="space-between" align="flex-start" gap={8}>
        <div className={styles.cardHeader}>
          <Typography.Text strong className={styles.name}>
            {record.displayed_name || "-"}
          </Typography.Text>
          <Typography.Text type="secondary" className={styles.subText}>
            {t("accounting.details.columns.booking_id")} - {record.booking_id || "-"}
          </Typography.Text>
        </div>
        <Flex align="center" gap={4} className={styles.headerRight}>
          <Typography.Text strong className={styles.amount}>
            {record.formatted_amount || formatCurrency(record.amount)}
          </Typography.Text>
          <ActionMenu record={record} onEdit={onEdit} onDelete={onDelete} />
        </Flex>
      </Flex>

      <div className={styles.divider} />

      <div className={styles.fieldGrid}>
        {fields.map((f) => (
          <Field key={f.key} label={f.label}>{f.value}</Field>
        ))}
      </div>
    </div>
  )
}

SalesCard.propTypes = {
  record: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  visibleOptional: PropTypes.instanceOf(Set).isRequired
}

function ExpenseCard({ record, onEdit, onDelete }) {
  const { t } = useTranslation()
  const lineItems = record.line_items || []

  return (
    <div className={styles.card}>
      <Flex justify="space-between" align="flex-start" gap={8}>
        <div className={styles.cardHeader}>
          <Typography.Text strong className={styles.name}>
            {(record.displayed_name || "-").replaceAll("\n", " ").trim()}
          </Typography.Text>
          {record.date && (
            <Typography.Text type="secondary" className={styles.subText}>
              {record.date}
            </Typography.Text>
          )}
        </div>
        <Flex align="center" gap={4} className={styles.headerRight}>
          <Typography.Text strong className={styles.amountExpense}>
            -{record.formatted_amount || formatCurrency(Math.abs(record.amount || 0))}
          </Typography.Text>
          <ActionMenu record={record} onEdit={onEdit} onDelete={onDelete} />
        </Flex>
      </Flex>

      {lineItems.length > 0 && (
        <>
          <div className={styles.divider} />
          <div className={styles.lineItemTable}>
            <div className={styles.lineItemHeader}>
              <span>{t("accounting.details.columns.item_name")}</span>
              <span>{t("accounting.details.columns.category")}</span>
              <span>{t("accounting.details.columns.amount_paid")}</span>
            </div>
            {lineItems.map((item) => (
              <div key={item.id} className={styles.lineItemBlock}>
                <div className={styles.lineItemField}>
                  <Typography.Text strong className={styles.lineItemLabel}>
                    {t("accounting.details.columns.item_name")}
                  </Typography.Text>
                  <Typography.Text className={styles.lineItemValue} ellipsis>
                    {(item.name || "-").replaceAll("\n", " ").trim()}
                  </Typography.Text>
                </div>
                <div className={styles.lineItemField}>
                  <Typography.Text strong className={styles.lineItemLabel}>
                    {t("accounting.details.columns.category")}
                  </Typography.Text>
                  <Typography.Text className={styles.lineItemValue} ellipsis>
                    {item.category?.name || "-"}
                  </Typography.Text>
                </div>
                <div className={`${styles.lineItemField} ${styles.lineItemFieldAmount}`}>
                  <Typography.Text strong className={styles.lineItemLabel}>
                    {t("accounting.details.columns.amount_paid")}
                  </Typography.Text>
                  <Typography.Text className={`${styles.lineItemValue} ${styles.amountExpense}`}>
                    {item.amount == null ? "-" : `-${formatCurrency(item.amount)}`}
                  </Typography.Text>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

ExpenseCard.propTypes = {
  record: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

export default function DetailsCardList({ items, isSales, onEdit, onDelete }) {
  const { t } = useTranslation()
  const [visibleOptional, setVisibleOptional] = useState(new Set())

  const toggleField = (key) =>
    setVisibleOptional((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  const addFieldsContent = isSales ? (
    <div style={{ minWidth: 180 }}>
      <Divider style={{ margin: "4px 0 8px" }} />
      {OPTIONAL_FIELD_KEYS.map((key) => (
        <div key={key} style={{ marginBottom: 6 }}>
          <Checkbox checked={visibleOptional.has(key)} onChange={() => toggleField(key)}>
            {t(`accounting.details.columns.${key === "booking_total_amount" ? "total_amount" : key}`)}
          </Checkbox>
        </div>
      ))}
    </div>
  ) : null

  if (!items.length) return null

  return (
    <>
      {isSales && (
        <Flex justify="flex-end" className="mt-s mb-xs">
          <Popover
            content={addFieldsContent}
            title={t("accounting.details.filters.add_columns")}
            trigger="click"
            placement="bottomRight"
          >
            <Button size="small" icon={<PlusOutlined />}>
              {t("accounting.details.filters.add_columns")}
            </Button>
          </Popover>
        </Flex>
      )}
      <div className={styles.list}>
        {items.map((record) =>
          isSales ? (
            <SalesCard key={record.id} record={record} onEdit={onEdit} onDelete={onDelete} visibleOptional={visibleOptional} />
          ) : (
            <ExpenseCard key={record.id} record={record} onEdit={onEdit} onDelete={onDelete} />
          )
        )}
      </div>
    </>
  )
}

DetailsCardList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  isSales: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}
